/**
 * CosmicQuestLedger - Live right-rail ticker of global game events.
 * Desktop-only (hidden below 1280px viewport). Pulls /api/feed/global-feed.
 *
 * Performance:
 *  - Polls every 60s (matches backend cache TTL × 2)
 *  - Pauses polling while tab is hidden, refreshes immediately on return
 *  - Does not mount on viewports < 1280px (where it's display:none anyway)
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trophy, Zap, Egg, Swords, Moon, Crown, Sparkles, Star, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60000;
const MIN_VIEWPORT_PX = 1280;

const ICON_MAP = {
  achievement: { Icon: Trophy, color: 'text-yellow-400' },
  level_up:    { Icon: Zap,    color: 'text-purple-400' },
  pet_hatch:   { Icon: Egg,    color: 'text-green-400' },
  boss_kill:   { Icon: Swords, color: 'text-red-400' },
  reading:     { Icon: Moon,   color: 'text-blue-400' },
  reincarnation: { Icon: Crown, color: 'text-amber-400' },
  rare_drop:   { Icon: Sparkles, color: 'text-pink-400' },
  milestone:   { Icon: Star,   color: 'text-cyan-400' },
  donation:    { Icon: Heart,  color: 'text-rose-400' },
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= MIN_VIEWPORT_PX
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsDesktop(window.innerWidth >= MIN_VIEWPORT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isDesktop;
};

export const CosmicQuestLedger = () => {
  const isDesktop = useIsDesktop();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('cq_ledger_collapsed') === '1'
  );

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/feed/global-feed`, { timeout: 5000 });
      const acts = res.data?.activities || [];
      setItems(acts.slice(0, 8));
    } catch {
      // silent fail — leave last known items
    }
  }, []);

  useEffect(() => {
    if (!isDesktop) return; // don't poll for users who can't see it
    let interval = null;

    const start = () => {
      if (interval) return;
      interval = setInterval(fetchFeed, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    fetchFeed();
    if (!document.hidden) start();

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fetchFeed();
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchFeed, isDesktop]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('cq_ledger_collapsed', next ? '1' : '0');
  };

  if (!isDesktop) return null;
  if (items.length === 0) return null;

  return (
    <aside
      className="hidden xl:flex fixed right-4 top-[88px] w-[240px] z-30 pointer-events-auto flex-col"
      data-testid="cosmic-quest-ledger"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-babylon-obsidian/90 border border-babylon-gold/30 rounded-t-lg backdrop-blur-sm">
        <span className="text-[10px] font-bold text-babylon-gold tracking-widest uppercase flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          Live Ledger
        </span>
        <button
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand ledger' : 'Collapse ledger'}
          data-testid="ledger-toggle"
          className="text-babylon-text-dim hover:text-babylon-gold text-xs transition-colors"
        >
          {collapsed ? '▾' : '▴'}
        </button>
      </div>

      {/* List */}
      {!collapsed && (
        <div className="bg-babylon-obsidian/80 border-x border-b border-babylon-gold/20 rounded-b-lg backdrop-blur-sm max-h-[60vh] overflow-y-auto custom-scrollbar">
          <AnimatePresence initial={false}>
            {items.map((item, idx) => {
              const cfg = ICON_MAP[item.type] || ICON_MAP.milestone;
              const { Icon, color } = cfg;
              const key = `${item.user_id || 'sys'}-${item.type}-${item.time || ''}-${idx}`;
              const body = (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  className="flex items-start gap-2 px-2.5 py-1.5 border-b border-babylon-gold/5 hover:bg-babylon-gold/5 transition-colors"
                  data-testid={`ledger-item-${idx}`}
                >
                  <Icon className={`w-3 h-3 mt-0.5 flex-shrink-0 ${color}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-babylon-text leading-snug line-clamp-2">
                      {item.message}
                    </p>
                    <p className="text-[9px] text-babylon-text-dim/70 mt-0.5">{item.time}</p>
                  </div>
                </motion.div>
              );
              return item.user_id
                ? <Link key={key} to={`/user/${item.user_id}`} className="block">{body}</Link>
                : body;
            })}
          </AnimatePresence>
        </div>
      )}
    </aside>
  );
};

export default CosmicQuestLedger;
